import { apiAgent } from "@/network/axiosAgent";
import type {
  CoverageAsset,
  CoverageHubDetail,
  CoverageHubRow,
  CoverageSummaryResponse,
  CoverageWorkFile,
  CoverageActivityRow,
  FetchCoverageActivityParams,
  FetchCoverageAssetsParams,
  FetchCoverageHubsParams,
  FetchCoverageWorkFilesParams,
  Page,
} from "./types";

const coverageBase = (slug: string) => `promote/board/${slug}/coverage`;

function toSearch(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function fetchCoverageSummary(slug: string) {
  return apiAgent
    .get<CoverageSummaryResponse>(`${coverageBase(slug)}/summary`)
    .then((res) => res.data);
}

export function fetchCoverageHubs(slug: string, params: FetchCoverageHubsParams = {}) {
  return apiAgent
    .get<Page<CoverageHubRow>>(
      `${coverageBase(slug)}/hubs${toSearch({
        search: params.search,
        status: params.status,
        sequence_status: params.sequence_status,
        output_ready: params.output_ready,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      })}`
    )
    .then((res) => res.data);
}

export function fetchCoverageActivity(slug: string, params: FetchCoverageActivityParams = {}) {
  return apiAgent
    .get<Page<CoverageActivityRow>>(
      `${coverageBase(slug)}/activity${toSearch({
        range: params.range ?? "1m",
        limit: params.limit ?? 10,
        offset: params.offset ?? 0,
      })}`
    )
    .then((res) => res.data);
}

export function fetchCoverageHubDetail(slug: string, hubId: number) {
  return apiAgent
    .get<CoverageHubDetail>(`${coverageBase(slug)}/hubs/${hubId}`)
    .then((res) => res.data);
}

export function fetchCoverageHubAssets(
  slug: string,
  hubId: number,
  params: FetchCoverageAssetsParams = {}
) {
  return apiAgent
    .get<Page<CoverageAsset>>(
      `${coverageBase(slug)}/hubs/${hubId}/assets${toSearch({
        content_role_id: params.content_role_id,
        temporal_phase: params.temporal_phase,
        task_id: params.task_id,
        limit: params.limit ?? 100,
        offset: params.offset ?? 0,
      })}`
    )
    .then((res) => res.data);
}

export function fetchCoverageHubWorkFiles(
  slug: string,
  hubId: number,
  params: FetchCoverageWorkFilesParams = {}
) {
  return apiAgent
    .get<Page<CoverageWorkFile>>(
      `${coverageBase(slug)}/hubs/${hubId}/work-files${toSearch({
        archived: params.archived,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      })}`
    )
    .then((res) => res.data);
}

export function removeCoverageContentRole(slug: string, assetId: number, tagId: number) {
  return apiAgent
    .delete<CoverageAsset>(
      `${coverageBase(slug)}/assets/${assetId}/content-roles/${tagId}`
    )
    .then((res) => res.data);
}

export function generateCoverageWorkFile(
  slug: string,
  body: { problem_hub_id: number; name: string }
) {
  return apiAgent.post(`promote/board/${slug}/marketing-work-files/generate`, body);
}
